import React, { useCallback } from 'react';

import { IItem } from '@rbi-ctg/menu';
import ActionButton from 'components/action-button';
import { ClickEventComponentNames, logButtonClick } from 'state/amplitude';
import { t } from 'state/intl/translations';
import { useProductWizardContext } from 'state/product-wizard-v2';
import { useProductWizardFlow } from 'state/product-wizard-v2/hooks/use-product-wizard-flow';
import { UserSelectionModifierSlotMap } from 'state/product-wizard-v2/types';

import { ProductCtaWrapper } from './product-cta-wrapper';
import { ActionsWrapper } from './product-cta.styled';
import { getModifierSelections, getSingleOrMultipleChoiceCtaBtnProps } from './utils';

interface IProductConfirmEditOrderChangesProps {
  modifierSelections?: UserSelectionModifierSlotMap;
  selectedItem: IItem | undefined;
}

export const ProductConfirmEditOrderChanges = ({
  modifierSelections,
  selectedItem,
}: IProductConfirmEditOrderChangesProps) => {
  const { goToReviewProduct, slotKeyToCustomize, isEdit } = useProductWizardFlow();
  const {
    submitCustomizations,
    singleOrMultipleModifierSelection,
    menuObject,
    setIsBackNavigation,
  } = useProductWizardContext();

  const defaultLabel = t('updateItem');

  const { disabledCta, labelCta } = React.useMemo(() => {
    if (singleOrMultipleModifierSelection?.[slotKeyToCustomize]) {
      const selections = getModifierSelections({
        isEdit,
        singleOrMultipleModifierSelection,
        modifierSelections,
        slotKeyToCustomize,
      });

      const { disabled, label } = getSingleOrMultipleChoiceCtaBtnProps({
        selections,
        singleOrMultipleModifierSelection,
        slotKeyToCustomize,
        type: menuObject._type,
      });
      return { disabledCta: disabled, labelCta: label };
    }

    return { disabledCta: false };
  }, [
    isEdit,
    menuObject._type,
    modifierSelections,
    singleOrMultipleModifierSelection,
    slotKeyToCustomize,
  ]);

  const buttonLabel = labelCta || defaultLabel;

  const handlePress = useCallback(() => {
    setIsBackNavigation(true);

    if (selectedItem && modifierSelections) {
      const modifierSelectionsEventAttr = Object.values(modifierSelections).map(
        modifierSelection => {
          const key = Object.keys(modifierSelection)[0];
          return modifierSelection[key].modifier.name?.locale ?? '';
        }
      );

      logButtonClick({
        attributes: {
          component: ClickEventComponentNames.PRODUCT_UPDATE_ITEM,
          name: selectedItem.name.locale ?? '',
          Id: selectedItem._id,
          modifierSelections: modifierSelectionsEventAttr,
        },
      });

      submitCustomizations({
        modifiers: modifierSelections,
        selectedItem,
        slotKey: slotKeyToCustomize,
      });
    }

    goToReviewProduct();
  }, [
    setIsBackNavigation,
    selectedItem,
    modifierSelections,
    goToReviewProduct,
    submitCustomizations,
    slotKeyToCustomize,
  ]);

  const Content = (
    <ActionsWrapper>
      <ActionButton
        disabled={disabledCta}
        flex={1}
        onPress={handlePress}
        testID="product-confirm-edit-order-changes"
      >
        {buttonLabel}
      </ActionButton>
    </ActionsWrapper>
  );

  return <ProductCtaWrapper elements={[{ element: Content }]} />;
};
